import { useEffect, useRef, useState } from 'react';
import { callStatSample, clientShell, peerSample, trackState, videoShowing, type ClientShell, type PeerSample, type StatsCounters, type TrackState } from '../utils/callStats';
import { installRtcRegistry, openPeerConnections } from '../utils/rtcRegistry';
import { saveCallStat } from '../services/liveCallBookings';
import { logger } from '../utils/logger';

// Before the wrapper makes its first connection: this module loads with the call.
installRtcRegistry();

/** How often the record goes to the server, and the overlay's own refresh while it shows. */
export const STATS_RECORD_MS = 10_000;
const OVERLAY_MS = 1_000;

export interface DiagnosticsPeer {
  id: string;
  name: string;
  streams: MediaStream[];
}

export interface DiagnosticsRow {
  peer: string;
  name: string;
  ice: string;
  connection: string;
  sample: PeerSample;
  video: TrackState & { enabled: boolean; readyState: string };
  audio: TrackState & { enabled: boolean; readyState: string };
  /** The lesson's other person (the sample is theirs). */
  main: boolean;
}

export type DiagnosticsSaved = { at: string } | { error: string } | null;

/** The page's own client, once. */
function thisClient(): { shell: ClientShell; ua: string } {
  const handlers = (window as unknown as { webkit?: { messageHandlers?: Record<string, unknown> } }).webkit?.messageHandlers;
  return { shell: clientShell(navigator.userAgent, !!handlers?.doubleBagel), ua: navigator.userAgent };
}

/**
 * The call's diagnostics (job 1, 30 Oct): every peer connection's stats and
 * each remote track's state, sampled every second while the overlay shows
 * and every 10 s otherwise; every 10 s a record under the call's session.
 * `videoEl` is the element showing the main peer's camera (and its sound).
 */
export function useCallDiagnostics(p: {
  open: boolean; from: string; bookingId?: string; sessionId?: string | null;
  /** This side, for the sample; a guest has none and writes none. */
  side?: 'coach' | 'player';
  peers: DiagnosticsPeer[]; mainPeer?: string; videoEl: () => HTMLVideoElement | null;
}): { rows: DiagnosticsRow[]; client: { shell: ClientShell; ua: string }; saved: DiagnosticsSaved } {
  const [client] = useState(thisClient);
  const [rows, setRows] = useState<DiagnosticsRow[]>([]);
  const [saved, setSaved] = useState<DiagnosticsSaved>(null);
  const latest = useRef(p);
  useEffect(() => { latest.current = p; });
  const counters = useRef(new WeakMap<RTCPeerConnection, StatsCounters>());
  const shown = useRef<{ el: HTMLVideoElement | null; presented: number | null; quality: number | null }>({ el: null, presented: null, quality: null });
  // Frames the element presented, counted by requestVideoFrameCallback where there is one.
  const presentedOn = useRef(new WeakMap<HTMLVideoElement, { n: number }>());
  const presented = (el: HTMLVideoElement): number | null => {
    const v = el as HTMLVideoElement & { requestVideoFrameCallback?: (cb: () => void) => number };
    if (typeof v.requestVideoFrameCallback !== 'function') return null;
    let c = presentedOn.current.get(el);
    if (!c) {
      const count = { n: 0 };
      c = count;
      presentedOn.current.set(el, count);
      const loop = () => { count.n += 1; v.requestVideoFrameCallback!(loop); };
      v.requestVideoFrameCallback(loop);
    }
    return c.n;
  };
  const lastRecord = useRef(0);

  // Which client this side is, in the log as the call starts (WKWebView or Safari on a phone).
  useEffect(() => { logger.info('Call client', { shell: client.shell, ua: client.ua }); }, [client]);

  useEffect(() => {
    let stopped = false;
    const tick = async () => {
      const { peers, mainPeer, videoEl, from, bookingId, sessionId, side } = latest.current;
      const now = Date.now();
      const el = videoEl();
      const elIds = el?.srcObject instanceof MediaStream ? el.srcObject.getTracks().map((t) => t.id) : null;
      // The element's own frames shown: what the 30 Oct phone never did (videoShowing picks the count).
      const nowPresented = el ? presented(el) : null;
      const nowQuality = el?.getVideoPlaybackQuality?.().totalVideoFrames ?? null;
      const same = !!el && shown.current.el === el;
      const counts = {
        presented: same && nowPresented !== null && shown.current.presented !== null ? [shown.current.presented, nowPresented] as [number, number] : null,
        quality: same && nowQuality !== null && shown.current.quality !== null ? [shown.current.quality, nowQuality] as [number, number] : null,
      };
      shown.current = { el: el ?? null, presented: nowPresented, quality: nowQuality };
      const next: DiagnosticsRow[] = [];
      for (const [i, pc] of openPeerConnections().entries()) {
        let report: RTCStatsReport;
        try { report = await pc.getStats(); } catch { continue; }
        const sample = peerSample(report.values() as Iterable<Record<string, unknown> & { type: string; id: string }>, counters.current.get(pc) ?? null, now);
        counters.current.set(pc, sample.counters);
        const ids = new Set(pc.getReceivers().map((r) => r.track?.id).filter(Boolean));
        const who = peers.find((x) => x.streams.some((s) => s.getTracks().some((t) => ids.has(t.id))));
        const camera = who?.streams.find((s) => s.getAudioTracks().length > 0 && s.getVideoTracks().length > 0) ?? null;
        const videoTrack = camera?.getVideoTracks()[0] ?? pc.getReceivers().find((r) => r.track?.kind === 'video')?.track ?? null;
        const audioTrack = camera?.getAudioTracks()[0] ?? pc.getReceivers().find((r) => r.track?.kind === 'audio')?.track ?? null;
        const main = !!who && who.id === mainPeer;
        next.push({
          peer: who?.id ?? `pc${i + 1}`,
          name: who?.name || `Peer ${i + 1}`,
          ice: pc.iceConnectionState,
          connection: pc.connectionState,
          sample,
          video: {
            ...trackState({ track: videoTrack, elementTrackIds: main ? elIds : null,
              producing: main && same ? videoShowing({ ...counts, decoded: sample.videoFlowing }) : sample.videoFlowing }),
            enabled: videoTrack?.enabled ?? false, readyState: videoTrack?.readyState ?? 'none',
          },
          audio: {
            ...trackState({ track: audioTrack, elementTrackIds: main ? elIds : null, producing: sample.audioFlowing }),
            enabled: audioTrack?.enabled ?? false, readyState: audioTrack?.readyState ?? 'none',
          },
          main,
        });
      }
      if (stopped) return;
      setRows(next);
      // One per 10-second slot: the rule refuses a second in the same one.
      if (bookingId && sessionId && side && now - lastRecord.current >= STATS_RECORD_MS - 200 && Math.floor(now / 10_000) !== Math.floor(lastRecord.current / 10_000)) {
        lastRecord.current = now;
        const { id, data } = callStatSample({ bookingId, sessionId, uid: from, side, nowMs: now, userAgent: client.ua, peer: next.find((r) => r.main) ?? next[0] ?? null });
        saveCallStat(id, data)
          .then(() => { if (!stopped) setSaved({ at: new Date(now).toISOString() }); })
          .catch((e: { code?: string }) => { if (!stopped) setSaved({ error: e?.code ?? 'failed' }); });
      }
    };
    void tick();
    const id = setInterval(() => { void tick(); }, p.open ? OVERLAY_MS : STATS_RECORD_MS);
    return () => { stopped = true; clearInterval(id); };
  }, [p.open, client]);

  return { rows, client, saved };
}

